/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../widgets/livewidget/nls/StringResources',
	'../../../util/DashboardFormatter',
	'../../../util/ArrayUtils',
	'./GridEdgeIterator',
	'./Summary',
	'../../../extensions/client-summaries/content-features/SummaryTypes',
	'../../../widgets/livewidget/query/QueryResultDataUtils'
], function (StringResources, Formatter, ArrayUtils, GridEdgeIterator, Summary, SummaryTypes, QueryResultDataUtils) {

	'use strict';

	const SUMMARY_ID = StringResources.get('summaryCaption');
	const NOT_AVAILABLE = StringResources.get('value_is_not_available');
	const UNDEFINED = 'undefined';
	const NONE = 'none';
	const OBJECT = 'object';
	const OVERALL = 'overall';

	const getUseValue = oValue => (oValue && typeof oValue.value !== UNDEFINED) ? oValue.value : oValue;

	const getKeys = (aData, bData) => {
		const aKeys = [];
		if (Array.isArray(aData)) {
			for (let i = 0; i < aData.length; i++) {
				aKeys.push(getUseValue(aData[i]));
			}
		}

		if (typeof bData !== UNDEFINED) {
			aKeys.push(getUseValue(bData));
		}
		return aKeys.toString();
	};

	/**
	 * !!! PERFORMANCE WARNING !!!
	 * This class manipulates potentially very large amounts of data on the client. Any line of code may be
	 * executed millions of times to render a single visualization, so should be as optimized as possible.
	 */
	class GridDataProcessor {
		constructor(options) {
			this._queryResult = options.queryResult;
			this._numberOfColumns = options.numberOfColumns || 0;
			this._lastGroupedColumnIndex = options.lastGroupedColumnIndex || 0;
			this._ungroupedColumnsInfo = [];
			this._rankDataItems = options.rankDataItems || [];
			this._columnSlot = options.columnSlot;
			this._columnDataItems = options.columnDataItems || [];
			this._getRankFormat = options.getRankFormat;
			this._getRealColumnIndex = options.getRealColumnIndex;
			this._valuesMap = {};
			this._exceedsColumnThreshold = options.exceedsColumnThreshold || false;
			this._numberOfAttributeColumns = this._numberOfColumns - (options.factColumnIndices ? options.factColumnIndices.length : 0);
			this._doSummary = options.doSummary;
			this.summaryAPI = options.summaryFeature;
			this.summaryNotSupported = this.summaryAPI && this.summaryAPI.isNotSupported();
			this._onCellDataChange = options.onCellDataChange;
			this._summaryValuePromises = [];

			this._factColumnsMap = {};
			Array.isArray(options.factColumnIndices) && options.factColumnIndices.forEach(index => this._factColumnsMap[index] = true);
		}

		/**
		 * onCellDataChange callback wrapper for formatted values
		 * @param {number} row row index
		 * @param {number} col column index
		 * @param {string} value cell value
		 * @param {object} formatSpec format specification for formatting the value
		 */
		onCellDataChange(row, col, value, formatSpec) {
			// CADBLOC-1398: summary value should show N/A when it is not available
			const rawValue = value && value.u ? value.u : value || NOT_AVAILABLE;
			this._onCellDataChange(row, col, Formatter.format(rawValue, formatSpec || null), rawValue);
		}

		_setUngroupedColumnsInfo() {
			for (let columnIndex = this._lastGroupedColumnIndex; columnIndex < this._numberOfColumns; columnIndex++) {
				let dataItem = this._columnDataItems[columnIndex];
				const isRankDataItem = dataItem.isRank && dataItem.isRank();
				let info = {
					'dataItemUniqueId': dataItem.getId(),
					'formatSpec': this._getSlotFormat(columnIndex),
					'id': dataItem.getId(),
					'index': columnIndex,
					'aggregationType': dataItem.getAggregation(),
					'addSummary': !isRankDataItem && this._columnSlot.getDataItemList()[this._getRealColumnIndex(columnIndex)].getAggregation() !== NONE
				};

				this._ungroupedColumnsInfo.push(info);
			}
		}

		_getSlotFormat(index) {
			let formatSpec;
			let dataItemList = this._columnSlot.getDataItemList();
			if (this._rankDataItems.length) {
				if (this._columnDataItems[index].isRank) {
					formatSpec = this._getRankFormat();
				} else {
					formatSpec = dataItemList[index - this._rankDataItems.length].getFormat();
				}
			} else {
				formatSpec = dataItemList[index].getFormat();
			}
			return formatSpec;
		}

		_generateNewDataTableWithOrder() {
			if (this._doSummary) {
				return this._preProcessedData.length ? this._preProcessedData : this._rawData;
			}

			for (let index = 0; index < this._rawData.length; index++) {

				const queryDataRow = this._rawData[index];
				let matchingIndex = -1;

				for (let i = 0; i < this._preProcessedData.length; i++) {
					const preProcessedDataRow = this._preProcessedData[i];
					if (!preProcessedDataRow) {
						continue;
					}

					if (this._rankDataItems.length > 0) {
						// in this scenario, only comparing edge rank value is sufficient since column index does not change and rank info is unique
						const preProcessedDataRowEdgeValue = preProcessedDataRow[0] && preProcessedDataRow[0].value;
						if (!preProcessedDataRowEdgeValue) {
							continue;
						}
						const queryDataRowEdgetRank = queryDataRow[0].value;
						if (!queryDataRowEdgetRank) {
							continue;
						}
						const preProcessedDataRowEdgeRank = preProcessedDataRowEdgeValue.deco && preProcessedDataRowEdgeValue.deco.rank;
						if (!preProcessedDataRowEdgeRank) {
							continue;
						}
						if (preProcessedDataRowEdgeRank === queryDataRowEdgetRank) {
							matchingIndex = i;
							break;
						}
					} else {
						// in this scenario, we need to compare the dataPoint value
						const preProcessedDataRowValue = preProcessedDataRow.map(item => {
							return (item.value && typeof item.value.value !== UNDEFINED) ? item.value.value : item.value;
						});

						const queryDataRowValue = [];
						for (let j = 0; j < queryDataRow.length; j++) {
							const row = queryDataRow[j];
							if (Array.isArray(row)) {
								for (let k = 0; k < row.length; k++) {
									if (typeof row[k].value !== UNDEFINED) {
										queryDataRowValue.push(row[k].value);
									}
								}
							} else if (typeof row.value !== UNDEFINED) {
								queryDataRowValue.push(row.value);
							}
						}
						if (ArrayUtils.isArrayInArray(queryDataRowValue, preProcessedDataRowValue)) {
							matchingIndex = i;
							break;
						}
					}
				}

				if (matchingIndex !== -1) {
					this._rawData[index] = this._preProcessedData[matchingIndex];
					this._preProcessedData[matchingIndex] = null;
				}
			}
			return this._rawData;
		}

		getData() {
			this._rawData = QueryResultDataUtils.getResolvedDataRows(this._queryResult);
			if (this._exceedsColumnThreshold) {
				// if ranking is applied or summary is disabled, we should respect the sort order of the query result
				return Promise.resolve(this._rawData);
			} else {
				// Promise-ifying this as a chain to allow time between the expensive steps for
				// the rest of the UI to react.  Long-running
				return Promise.resolve()
					.then(this._setUngroupedColumnsInfo.bind(this))
					.then(this._generateIntermediateObjects.bind(this))
					.then(this._generateNewDataTableWithSummaries.bind(this))
					.then(this._generateNewDataTableWithOrder.bind(this));
			}
		}

		/*
		 * Generates grouped columns traversal object and a values map containing summaries.
		 */
		_generateIntermediateObjects() {
			//an intermediate object which the iterator will traverse to get grouped column and summaries info
			//used to generate the keys for indexing into the values map.
			const dataRowCount = this._queryResult.getRowCount();
			let container = {};
			let attributeRowValues;
			for (let rowIdx = 0; rowIdx < dataRowCount; rowIdx++) {
				attributeRowValues = this._getAttributeValuesInTheRow(rowIdx);
				this._addRowValuesToGroupedItemsContainer(container, attributeRowValues, rowIdx);
				this._generateIntermediateObjectsForUngroupedColumns(attributeRowValues, rowIdx);
			}
			return this._addSortKeysToContainer(container);
		}

		_generateIntermediateObjectsForUngroupedColumns(attributeRowValues, rowIdx) {
			let ungroupedColumnInfo, value, rowValue, cellIdx, key, useValue, end, summaryKey, currentSummaryValue;
			for (let idx = 0, j = this._lastGroupedColumnIndex; j < this._numberOfColumns; j++, idx++) {
				ungroupedColumnInfo = this._ungroupedColumnsInfo[idx];
				value = this._getCellValue(rowIdx, j);
				cellIdx = `${rowIdx}:${j}`;
				if (typeof value !== UNDEFINED) {
					useValue = getUseValue(value);
					if (typeof useValue !== UNDEFINED && useValue !== null) {
						rowValue = attributeRowValues.concat(ungroupedColumnInfo.dataItemUniqueId);
						key = getKeys(rowValue);
						this._valuesMap[key] = { value, cellIdx };
						if (ungroupedColumnInfo.addSummary && ungroupedColumnInfo.aggregationType) {
							const keyUIds = {
								summaryKeys: [],
								oCategoryAndTupleUIds: {},
								isLocalSummary: this._isLocalSummary(ungroupedColumnInfo.aggregationType)
							};

							// groups not including the last attribute and measure
							end = rowValue.length - 2;
							for (; end >= 0; --end) {
								rowValue[end] = SUMMARY_ID;
								summaryKey = getKeys(rowValue);
								keyUIds.summaryKeys.push(summaryKey);
								const categoryAndTupleUId = {
									categoryUIds: [],
									tupleUIds: []
								};

								if (!(keyUIds.isLocalSummary || this.summaryNotSupported || keyUIds.oCategoryAndTupleUIds[summaryKey])) {
									for (let i = 0; i < this._columnDataItems.length; i++) {
										if (typeof rowValue[i] === OBJECT) {
											categoryAndTupleUId.categoryUIds.push(this._columnDataItems[i].getId());
											categoryAndTupleUId.tupleUIds.push(rowValue[i].value);
										}
									}
								}
								keyUIds.oCategoryAndTupleUIds[summaryKey] = categoryAndTupleUId;

								currentSummaryValue = this._valuesMap[summaryKey];
								if (!currentSummaryValue && ungroupedColumnInfo) {
									currentSummaryValue = new Summary(ungroupedColumnInfo, keyUIds.isLocalSummary);
									if (!this.summaryNotSupported && !keyUIds.isLocalSummary) {
										// Add call back context to update values once resolved
										currentSummaryValue.addCallBackContext({
											categoryUIds: keyUIds.oCategoryAndTupleUIds[summaryKey].categoryUIds,
											tupleUIds: keyUIds.oCategoryAndTupleUIds[summaryKey].tupleUIds,
											cbOnCellDataChange: this.onCellDataChange.bind(this),
											cbGetSummary: this.summaryAPI ? this.summaryAPI.getSummary.bind(this.summaryAPI) : undefined,
											cbAddSummaryValuePromise: this.cacheSummaryValuePromise.bind(this)
										});
									}
								}

								if (currentSummaryValue) {
									currentSummaryValue.addValue(useValue);
								}
								this._valuesMap[summaryKey] = currentSummaryValue;
							}
						}
					}
				}
			}
		}

		/**
		 * Retrieve a cell value for grid
		 *
		 * @param {number} rowIdx - the row index value
		 * @param {number} columnIdx - the column index value
		 *
		 * @return {object} the cell value object
		 */
		_getCellValue(rowIdx, columnIdx) {
			let value = this._queryResult.getValue(rowIdx, columnIdx);
			return ((value instanceof Array) && !!value.length) ? value[0] : value;
		}

		_getAttributeValuesInTheRow(rowIdx) {
			let attributeRowValues = [];
			for (let colIdx = 0; colIdx < this._numberOfColumns; colIdx++) {
				if (!this._factColumnsMap[colIdx]) {
					attributeRowValues.push(this._getCellValue(rowIdx, colIdx));
				}
			}
			return attributeRowValues;
		}

		/**
		 * Returns a new object with Summary as the  last item to be used for iterating
		 */
		_addSortKeysToContainer(edge) {
			let hasSummary = false;
			const newEdgeObject = {
				edge,
				sortedKeys: []
			};

			for (let key in edge) {
				if (edge.hasOwnProperty(key)) {
					if (key === SUMMARY_ID) {
						hasSummary = true;
					} else {
						newEdgeObject.sortedKeys.push(key);
						let nestedEdges = edge[key].nestedEdges;
						if (nestedEdges) {
							edge[key].nestedEdges = this._addSortKeysToContainer(nestedEdges);
						}
					}
				}
			}
			if (hasSummary) {
				newEdgeObject.sortedKeys.push(SUMMARY_ID);
				let nestedEdges = edge[SUMMARY_ID].nestedEdges;
				if (nestedEdges) {
					edge[SUMMARY_ID].nestedEdges = this._addSortKeysToContainer(nestedEdges);
				}
			}
			return newEdgeObject;
		}

		_addRowValuesToGroupedItemsContainer(container, attributeRowValues, rowIndex, columnIndex, ancestor, attributeRowValueIndex = 0) {
			columnIndex = columnIndex || 0;
			if (attributeRowValues && attributeRowValues.length > 0) {
				let rowValue = attributeRowValues[attributeRowValueIndex].value;
				if (!container[rowValue]) {
					container[rowValue] = {
						value: attributeRowValues[attributeRowValueIndex],
						level: columnIndex,
						cellIdx: `${rowIndex}:${columnIndex}`
					};

					if (this._doSummary) {
						this._addSummaryToGroupedItemsContainer(container, columnIndex, ancestor);
					}
				}

				const nextAttributeRowValueIndex = attributeRowValueIndex + 1;
				if (nextAttributeRowValueIndex < attributeRowValues.length) {
					if (!container[rowValue].nestedEdges) {
						container[rowValue].nestedEdges = {};
					}
					this._addRowValuesToGroupedItemsContainer(container[rowValue].nestedEdges, attributeRowValues, rowIndex, columnIndex + 1, rowValue, nextAttributeRowValueIndex);
				}
			}
		}

		_addSummaryToGroupedItemsContainer(container, columnIndex, ancestor, bIsOverallSummary) {
			let isOverallSummary = bIsOverallSummary || columnIndex === 0;
			if (columnIndex < this._numberOfAttributeColumns) {
				let summaryNode = {
					isSummary: true,
					level: columnIndex,
					isOverallSummary: isOverallSummary,
					summaryOf: (isOverallSummary) ? OVERALL : ancestor
				};

				container[SUMMARY_ID] = summaryNode;

				if (columnIndex < (this._numberOfAttributeColumns - 1)) {
					//special handling for overall summary in order for the crosstab to render the summary column correctly
					//need to add as many summary as nested levels
					summaryNode.nestedEdges = {};
					this._addSummaryToGroupedItemsContainer(summaryNode.nestedEdges, columnIndex + 1, ancestor, isOverallSummary);
				}
			}
		}

		/**
		 * caching the summary value promise
		 *
		 * @param {object} summary value promise; when resolved the summary value is available
		 */
		cacheSummaryValuePromise(promise) {
			this._summaryValuePromises.push(promise);
		}

		/**
		 * Returns a promise indicating whether all summaries get in the grid
		 */
		whenAllSummaryValuesAreReady() {
			return Promise.all(this._summaryValuePromises);
		}

		_getSummaryKeys(attributeRowValues) {
			const summaryKeys = {};
			//rowStart/rowEnd indicates starting/ending position in the originalDimension array that contains row info
			let rowEnd = this._numberOfAttributeColumns - 1;
			for (let rowStart = 0; rowEnd >= rowStart; --rowEnd) {
				attributeRowValues[rowEnd] = SUMMARY_ID;
				summaryKeys[getKeys(attributeRowValues)] = true;
			}
			return Object.keys(summaryKeys);
		}

		_generateNewDataTableWithSummaries(groupedColumnsTraversalObject) {
			let dataWithSummary = [];
			let nextGroupedItem;
			let dataTableRow;
			let rowIndex = 0;

			let groupedColumnsRowIterator = new GridEdgeIterator(groupedColumnsTraversalObject, undefined);
			while ((nextGroupedItem = groupedColumnsRowIterator.next())) {
				dataTableRow = [];
				this._addGroupedItemsToRow(dataTableRow, nextGroupedItem);
				this._addValuesToRow(dataTableRow, nextGroupedItem, ++rowIndex);
				dataWithSummary.push(dataTableRow);
			}
			this._preProcessedData = dataWithSummary;
		}

		_addGroupedItemsToRow(dataTableRow, firstGroupedItem) {
			let nextColumn = firstGroupedItem;
			let columnIndex = 0;
			while (nextColumn && (columnIndex < this._lastGroupedColumnIndex)) {
				dataTableRow.push(nextColumn);
				nextColumn = nextColumn.subCategory;
				columnIndex++;
			}
		}

		/*
		 *  This function adds fact value or attribute value which is not grouped.
		 */
		_addValuesToRow(dataTableRow, firstGroupedItem, rowIndex) {

			for (let columnIndex = 0; columnIndex < this._ungroupedColumnsInfo.length; columnIndex++) {
				let nextColumn = this._ungroupedColumnsInfo[columnIndex];

				let key = getKeys(firstGroupedItem.descendant_or_self, nextColumn.dataItemUniqueId);
				let valueObj = this._valuesMap[key] || null;
				let data = null;
				let rawValue = null;
				if (valueObj) {
					if (valueObj.getSummarizedValue) {
						if (!valueObj._isLocalSummary) {
							data = valueObj.getSummarizedValue(rowIndex, nextColumn.index);
						} else if (valueObj.getValue() !== null) {
							rawValue = valueObj.getValue();
							data = Formatter.formatNull(rawValue, valueObj._formatSpec || null);
						} else {
							data = valueObj.value;
						}
					} else {
						data = valueObj.value;
					}
				}

				let result = data;

				if (!result) {
					//for summary of attribute column which is ungrouped
					if (firstGroupedItem.isSummary && !nextColumn.aggregationType) {
						result = '  ';
					}
				}

				dataTableRow.push({
					isSummary: firstGroupedItem.isSummary,
					value: result,
					rawValue: rawValue,
					key: key,
					cellIdx: (valueObj) ? valueObj.cellIdx : null,
					isOverallSummary: firstGroupedItem.isOverallSummary
				});
			}
		}

		_isLocalSummary(aggregationType) {
			return SummaryTypes.indexOf(aggregationType) === -1;
		}

	}

	return GridDataProcessor;
});